import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Download, Trash2, Shield, Info, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../services/api';

interface ConsentData {
  privacy_consented_at: string | null;
  tos_consented_at: string | null;
  marketing_consent: boolean;
  privacy_policy_version: string;
  data_residency: string;
  privacy_officer_email: string;
}

const Privacy = () => {
  const { t } = useTranslation('privacy');
  const navigate = useNavigate();

  const [consents, setConsents] = useState<ConsentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchConsents();
  }, []);

  const fetchConsents = async () => {
    try {
      const res = await api.get<ConsentData>('/privacy/consents');
      const data = (res as any).data || res;
      setConsents(data);
      setMarketingConsent(data.marketing_consent);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await api.put('/privacy/consents', { marketing_consent: marketingConsent });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // handled by API interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await api.get('/privacy/export');
      const data = (res as any).data || res;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vivosign-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // handled by API interceptor
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.post('/privacy/delete-account', { password: deletePassword });
      localStorage.removeItem('auth_token');
      navigate('/login');
    } catch (err: any) {
      setDeleteError(err?.message || err?.errors?.password?.[0] || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('not_consented');
    return t('consented_on', { date: new Date(dateStr).toLocaleDateString() });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Consent Status */}
      <section className="rounded-lg border p-5 bg-white">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">{t('consent_section_title')}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">{t('consent_section_description')}</p>

        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium">{t('privacy_policy_consented')}</span>
            <span className="text-slate-500">{formatDate(consents?.privacy_consented_at ?? null)}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium">{t('tos_consented')}</span>
            <span className="text-slate-500">{formatDate(consents?.tos_consented_at ?? null)}</span>
          </div>

          <div className="border-t pt-3 mt-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium">{t('marketing_consent_label')}</span>
                <p className="text-xs text-slate-500">{t('marketing_consent_description')}</p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSavePreferences} disabled={saving}>
              {saving && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
              {t('save_preferences')}
            </Button>
            {saveSuccess && <span className="text-sm text-green-600">{t('preferences_saved')}</span>}
          </div>
        </div>
      </section>

      {/* Data Export */}
      <section className="rounded-lg border p-5 bg-white">
        <div className="flex items-center gap-2 mb-1">
          <Download className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">{t('data_section_title')}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">{t('data_section_description')}</p>
        <p className="text-sm text-slate-600 mb-3">{t('export_data_description')}</p>

        <Button variant="outline" onClick={handleExportData} disabled={exporting}>
          {exporting ? (
            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{t('exporting')}</>
          ) : (
            <><Download className="h-4 w-4 mr-1.5" />{t('export_data')}</>
          )}
        </Button>
      </section>

      {/* Delete Account */}
      <section className="rounded-lg border border-red-200 p-5 bg-red-50/30">
        <div className="flex items-center gap-2 mb-1">
          <Trash2 className="h-5 w-5 text-red-600" />
          <h3 className="font-semibold text-red-900">{t('delete_section_title')}</h3>
        </div>
        <p className="text-sm text-red-700 mb-4">{t('delete_section_description')}</p>

        {!showDeleteModal ? (
          <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)}>
            <Trash2 className="h-4 w-4 mr-1.5" />
            {t('delete_account')}
          </Button>
        ) : (
          <div className="space-y-3 p-4 rounded-lg bg-white border border-red-200">
            <h4 className="font-medium text-red-900">{t('delete_confirm_title')}</h4>
            <p className="text-sm text-slate-600">{t('delete_confirm_message')}</p>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('delete_password_label')}</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            {deleteError && (
              <p className="text-sm text-red-600">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(null); }}>
                {t('delete_cancel')}
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDeleteAccount} disabled={deleting || !deletePassword}>
                {deleting ? (
                  <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />{t('deleting')}</>
                ) : (
                  t('delete_confirm')
                )}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Privacy Information */}
      <section className="rounded-lg border p-5 bg-slate-50">
        <div className="flex items-center gap-2 mb-1">
          <Info className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold">{t('info_section_title')}</h3>
        </div>

        <div className="space-y-2 text-sm mt-3">
          <div className="flex justify-between">
            <span className="text-slate-500">{t('data_residency_label')}</span>
            <span className="font-medium">{consents?.data_residency || 'Canada'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">{t('policy_version_label')}</span>
            <span className="font-medium">v{consents?.privacy_policy_version || '1.0'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">{t('privacy_officer_label')}</span>
            <span className="font-medium">
              {consents?.privacy_officer_email
                ? t('privacy_officer_contact', { email: consents.privacy_officer_email })
                : t('no_privacy_officer')}
            </span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-white rounded border text-xs text-slate-500 space-y-1">
          <p>{t('pipeda_notice')}</p>
          <p>{t('opc_notice')}</p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
